import { logger } from "@rshbintech.dboul/logger";
import { SettingsItem } from "../services";
import {
  getUserCredentials,
  checkIsLoginPage,
  checkIsLoginZonePage,
  checkIsMainPage,
} from "../common/utils/general";
import { LOGIN_PAGE_PATHNAME } from "../common/constants/general";
import {
  debounce,
  decodeBase64String,
  checkIsMiniLk,
} from "../common/utils/auth";
import {
  EVENT_TYPES,
  AUTH_ZONES,
  ACTOR_TYPES,
  ADAPTIVE,
  CURRENT_AUTH_ZONE,
  EXPIRATION_DATE_OFFSET_IN_SECONDS,
  HAS_REFRESH_TOKEN,
  HAS_SSO_REFRESH_TOKEN,
  REDIRECT_QUERY_PARAM,
  MINI_LK,
  MS_LOGIN_DEBUG,
  MS_TOKEN_ISSUER,
} from "../common/constants/auth";
import { ActorType, TokenPayload, EventTypes } from "../common/types/general";

async function getMonolithApi() {
  const { monolithApi } = await import("../services");

  return monolithApi;
}

async function getAuthenticationApi() {
  const { authenticationApi } = await import("../services");

  return authenticationApi;
}

async function getSettingsApi() {
  const { settingsApi } = await import("../services");

  return settingsApi;
}

let accessToken = "";
let accessTokenExpirationDate: number | null = null;

let onUserLoginCallbacks: Array<() => unknown> = [];
let onUserLogoutCallbacks: Array<() => unknown> = [];

export class AuthService {
  private static subscribers: Record<EventTypes, (() => void)[]> = {
    [EVENT_TYPES.ON_ACCESS_TOKEN_RECEIVED]: [],
  };

  private static login = "";

  private static password = "";

  /**
   * INFO: Свойство определяющее необходимость отмены запросов в очереди.
   *
   * Используется при логауте или когда один из запросов получил ответ с кодом 401 и нам необходимо отменить
   * все последующие запросы в очереди и сделать редирект пользователя на страницу Логина (см. использование AuthService.resetAuthData).
   */
  private static needToCancelRequestsInQueue = false;

  /**
   * INFO: Свойство определяющее необходимость отмены всех последующих редиректов на страницу Логина.
   *
   * Существует несколько сценариев приводящих к редиректу пользователя на страницу Логина:
   *
   * http-клиент:
   *  1) Ответы с кодом 401.
   *
   * shared-ui:
   *  1) Синхронизация процесса логаута во всех открытых вкладках пользователя;
   *  2) Попытка входа в авторизованную зону приложения с флагом в hasRefreshToken: false в local storage.
   *
   * Если данные сценарии сработают одновременно, то у нас произойдет несколько редиректов на страницу Логина,
   * при этом каждый редирект будет отменять предыдущий, что можно увидеть в инструментах разработчика в браузере.
   *
   * Данным свойством мы определяем что процесс редиректа уже был запущен каким-либо участком кода,
   * что позволит игнорировать последующие вызовы редиректа на страницу Логина.
   */
  private static hasAlreadyRedirectedToLoginPage = false;

  /**
   * INFO: флаг сингнализирующий об отсутвии полномочий.
   * Для монолита выставляется при ответе с кодом 401 и ошибкой "Вы не можете выполнить вход в Систему. Ваши полномочия истекли"
   */
  private static noPermissions = false;

  private static loginPageNextParam = "";

  /**
   * Микросервисы - публичные настройки
   */
  private static microServicesPublicSettings: SettingsItem[] | undefined =
    undefined;

  private static getMicroServicesPublicSettings = async () => {
    try {
      const settingsApi = await getSettingsApi();
      const response = await settingsApi.api.getPublicProperties();
      const { data } = response;

      return data;
    } catch (error) {
      logger.sendError(error);
    }
  };

  private static isLoggingDebug = async () => {
    if (!AuthService.microServicesPublicSettings) {
      const response = await AuthService.getMicroServicesPublicSettings();

      if (response) {
        AuthService.microServicesPublicSettings = response;
      }
    }

    return AuthService.microServicesPublicSettings?.some((setting) =>
      setting.key === MS_LOGIN_DEBUG ? setting.value === "true" : false
    );
  };

  public static setLoginPageNextParam = (path: string) => {
    const isLoginZonePage = checkIsLoginZonePage();
    const isMainPage = checkIsMainPage();

    if (isLoginZonePage || isMainPage) {
      return;
    }

    AuthService.loginPageNextParam = `?${REDIRECT_QUERY_PARAM}=${path}`;
  };

  public static redirectToLoginPage = () => {
    if (AuthService.hasAlreadyRedirectedToLoginPage) {
      return;
    }

    const isLoginPage = checkIsLoginPage();

    if (isLoginPage) {
      return;
    }

    AuthService.hasAlreadyRedirectedToLoginPage = true;

    window.location.href = LOGIN_PAGE_PATHNAME + AuthService.loginPageNextParam;
  };

  private static cancelRequestsInQueue = () => {
    AuthService.needToCancelRequestsInQueue = true;
  };

  public static get isNeedToCancelRequestsInQueue() {
    return AuthService.needToCancelRequestsInQueue;
  }

  public static get hasNoPermissions() {
    return AuthService.noPermissions;
  }

  public static set hasNoPermissions(value: boolean) {
    AuthService.noPermissions = value;
  }

  public static onUserLogin = (callback: () => unknown) => {
    onUserLoginCallbacks.push(callback);
  };

  public static onUserLogout = (callback: () => unknown) => {
    onUserLogoutCallbacks.push(callback);
  };

  private static executeOnUserLoginCallbacks = () => {
    if (onUserLoginCallbacks.length) {
      onUserLoginCallbacks.forEach((callback) => callback());
      onUserLoginCallbacks = [];
    }
  };

  private static executeOnUserLogoutCallbacks = () => {
    if (onUserLogoutCallbacks.length) {
      onUserLogoutCallbacks.forEach((callback) => callback());
      onUserLogoutCallbacks = [];
    }
  };

  public static setUserCredentials = (
    { login, password }: Record<string, string>,
    isMSCredentials: boolean
  ) => {
    AuthService.login = isMSCredentials ? login : decodeBase64String(login);
    AuthService.password = isMSCredentials
      ? password
      : decodeBase64String(password);
  };

  public static getSsoRefreshTokenByLoginPassword = async (token?: string) => {
    if (!token) {
      return;
    }

    const { isOtpFlag } = AuthService.getTokenPayload(token);

    if (isOtpFlag === true && AuthService?.login && AuthService?.password) {
      try {
        const { login, password } = getUserCredentials(
          AuthService.login,
          AuthService.password
        );

        const authenticationApi = await getAuthenticationApi();

        await authenticationApi.api.getSsoRefreshTokenByLoginPassword({
          login,
          password,
        });

        AuthService.setHasSsoRefreshToken("true");
      } catch (error) {
        /**
         * INFO: В случае ошибки запроса SSO мы просто не устанавливаем флаг hasSsoRefreshToken
         * в local storage, но позволяем пользователю войти в приложение.
         */
      }
    }
  };

  private static getTokenPayload(accessToken: string) {
    const tokenPayload = accessToken.split(".")[1];
    const decodedTokenPayload = atob(tokenPayload);
    const parsedTokenPayload = JSON.parse(decodedTokenPayload) as TokenPayload;

    return parsedTokenPayload;
  }

  public static async setAccessToken(jwt: string) {
    accessToken = jwt;

    AuthService.notifyListeners(EVENT_TYPES.ON_ACCESS_TOKEN_RECEIVED);

    try {
      const { exp, isOtpFlag, actorType } = AuthService.getTokenPayload(jwt);

      /**
       * INFO: При наличии в токене поля actorType, в localStorage устанавливается флаг
       * currentAuthZone определяющий доступную для пользователя авторизационную зону.
       *
       * На данный момент это могут быть страница логина, главная и страница выбора организации.
       *
       * Свойство токена actorType со значением USER, устанавливается только после прохождения 1-го фактора.
       *
       * В данном случае мы устаналиваем авторизационную зону - страница выбора организации.
       * Далее делаем редирект на страницу выбора организации в AuthProvider из проекта shared-ui.
       */
      if (actorType === ACTOR_TYPES.USER) {
        AuthService.setCurrentAuthZone(actorType);
        AuthService.setAccessTokenExpirationDate(exp);

        return;
      }

      /**
       * INFO: Свойства токена isOtpFlag со значением true, устанавливается после
       * прохождения 2-го фактора (подтверждение смс-кодом) для пользователя-смсника,
       * и после успешного выполнения запроса /login для пользователя-токениста.
       */
      if (isOtpFlag === true) {
        /**
         * INFO: Временное решение по дополнительному вызову МС Аутентификация,
         * для демонстрации работы SSO (аутентификация через форму логина СБ и редирект в СФ).
         * В будущем данная функциональность должна быть мигрирована в проект страницы Логина.
         */

        AuthService.resetUserCredentials();
        AuthService.setCurrentAuthZone(actorType);
        AuthService.setHasRefreshToken("true");
        AuthService.setAccessTokenExpirationDate(exp);
        AuthService.executeOnUserLoginCallbacks();
      }
    } catch (error) {
      if (await AuthService.isLoggingDebug()) {
        logger.sendError(error);
      }

      throw error;
    }
  }

  public static getAccessToken() {
    return accessToken;
  }

  public static get hasAccessToken() {
    return Boolean(accessToken);
  }

  public static getTokenIssuerInfo() {
    const jwt = AuthService.getAccessToken();

    if (jwt) {
      const { iss, monolithClientId, isPilot, exp } =
        AuthService.getTokenPayload(jwt);

      return {
        exp,
        isPilot,
        monolithClientId,
        isMonolithIssuer: iss !== MS_TOKEN_ISSUER,
      };
    }

    return null;
  }

  private static resetUserCredentials() {
    AuthService.login = "";
    AuthService.password = "";
  }

  private static resetAccessToken() {
    accessToken = "";
  }

  private static resetAccessTokenExpirationDate() {
    accessTokenExpirationDate = null;
  }

  private static resetIsAdaptive() {
    if (!AuthService.isAdaptive) {
      return;
    }

    localStorage.removeItem(ADAPTIVE);
  }

  private static resetIsMiniLk() {
    if (!AuthService.isMiniLk) {
      return;
    }

    localStorage.removeItem(MINI_LK);
  }

  private static setAccessTokenExpirationDate(expirationDate: number) {
    if (typeof expirationDate === "number") {
      accessTokenExpirationDate = expirationDate;
    }
  }

  private static get isAccessTokenExpired() {
    if (accessTokenExpirationDate === null) {
      return true;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const accessTokenExpirationDateWithOffset =
      accessTokenExpirationDate - EXPIRATION_DATE_OFFSET_IN_SECONDS;

    const isAccessTokenExpired =
      accessTokenExpirationDateWithOffset <= nowInSeconds;

    return isAccessTokenExpired;
  }

  public static get isAdaptive() {
    return localStorage.getItem(ADAPTIVE) === "true";
  }

  public static get isMiniLk() {
    return localStorage.getItem(MINI_LK) === "true";
  }

  public static get hasRefreshToken() {
    return localStorage.getItem(HAS_REFRESH_TOKEN) === "true";
  }

  public static get hasSsoRefreshToken() {
    return localStorage.getItem(HAS_SSO_REFRESH_TOKEN) === "true";
  }

  /**
   * INFO: Временное свойство необходимое для этапа миграции процесса логина с Монолита на МС Аутентификации.
   *
   * На основании флага currentAuthZone мы предполагаем что процесс логина должен происходить через МС Аутентификации.
   * По аналогии с флагом hasRefreshToken, который мы используем для запуска процесса логина через Монолит.
   */
  public static get hasCurrentAuthZone() {
    return Boolean(localStorage.getItem(CURRENT_AUTH_ZONE));
  }

  public static get currentAuthZone() {
    return localStorage.getItem(CURRENT_AUTH_ZONE);
  }

  /**
   * TODO: Удалить после миграции процесса логина с Монолита на МС Аутентификации
   */
  private static setHasRefreshToken(status: "true" | "false") {
    localStorage.setItem(HAS_REFRESH_TOKEN, status);
  }

  private static setHasSsoRefreshToken(status: "true" | "false") {
    localStorage.setItem(HAS_SSO_REFRESH_TOKEN, status);
  }

  private static setCurrentAuthZone(actorType?: ActorType) {
    if (!actorType) {
      return;
    }

    const isUserActorType = actorType === ACTOR_TYPES.USER;
    const isDelegateActorType = actorType === ACTOR_TYPES.DELEGATE;

    if (isUserActorType) {
      /**
       * Мобильных/планшетных пользователей направляем на страницу Мини-ЛК
       */
      if ((AuthService.isAdaptive || AuthService.isMiniLk) && checkIsMiniLk()) {
        localStorage.setItem(CURRENT_AUTH_ZONE, AUTH_ZONES.MINI_LK_PAGE);
      } else {
        localStorage.setItem(
          CURRENT_AUTH_ZONE,
          AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE
        );
      }

      return;
    }

    if (isDelegateActorType) {
      localStorage.setItem(CURRENT_AUTH_ZONE, AUTH_ZONES.MAIN_PAGE);

      return;
    }
  }

  /**
   * TODO: Удалить после миграции процесса логина с Монолита на МС Аутентификации
   */
  private static resetHasRefreshToken() {
    AuthService.setHasRefreshToken("false");
  }

  private static resetHasSsoRefreshToken() {
    AuthService.setHasSsoRefreshToken("false");
  }

  private static resetCurrentAuthZone() {
    if (!AuthService.hasCurrentAuthZone) {
      return;
    }

    localStorage.removeItem(CURRENT_AUTH_ZONE);
  }

  // TODO: Сейчас /refresh-token работает некорректно, необходимо проконтролировать исправление (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-102)
  private static async updateRefreshAndAccessTokens() {
    try {
      /**
       * INFO: Проверяем с какой системой нам необходимо работать, с МС Аутентификации или с Монолитом
       */
      if (AuthService.hasCurrentAuthZone) {
        const authenticationApi = await getAuthenticationApi();
        const refreshTokenResponse =
          await authenticationApi.api.getAccessTokenByRefreshToken();

        if (refreshTokenResponse.data.access_token) {
          await AuthService.setAccessToken(
            refreshTokenResponse.data.access_token
          );
        }
        /**
         * TODO: Удалить данный блок кода после миграции процесса логина с Монолита на МС Аутентификации
         */
      } else {
        const monolithApi = await getMonolithApi();
        const refreshTokenResponse = await monolithApi.auth.refreshTokenCreate({
          expiredAccessToken: AuthService.getAccessToken(),
        });

        if (refreshTokenResponse.data.jwt) {
          await AuthService.setAccessToken(refreshTokenResponse.data.jwt);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  public static updateAuthData = debounce(
    AuthService.updateRefreshAndAccessTokens,
    100
  );

  public static get needUpdateAuthData() {
    return (
      (!AuthService.hasAccessToken || AuthService.isAccessTokenExpired) &&
      (AuthService.hasRefreshToken || AuthService.hasCurrentAuthZone)
    );
  }

  public static resetAuthData() {
    AuthService.cancelRequestsInQueue();

    AuthService.resetAccessToken();
    AuthService.resetUserCredentials();
    AuthService.resetHasRefreshToken();
    AuthService.resetHasSsoRefreshToken();
    AuthService.resetCurrentAuthZone();
    AuthService.resetIsAdaptive();
    AuthService.resetIsMiniLk();
    AuthService.resetAccessTokenExpirationDate();

    AuthService.executeOnUserLogoutCallbacks();
    AuthService.hasNoPermissions = false;

    /**
     * TODO: Подумать о добавлении вызова AuthService.redirectToLoginPage, поскольку
     * после вызова AuthService.resetAuthData всегда следует вызов редиректа на страницу Логина.
     */
  }

  private static notifyListeners(type: EventTypes) {
    AuthService.subscribers[type].forEach((subscriber) => {
      subscriber();
    });
  }

  private static onAccessTokenReceived(listener: () => void) {
    if (AuthService.hasAccessToken) {
      listener();
    }

    AuthService.subscribers[EVENT_TYPES.ON_ACCESS_TOKEN_RECEIVED].push(
      listener
    );
  }

  public static addEventListener(type: EventTypes, listener: () => void) {
    switch (type) {
      case EVENT_TYPES.ON_ACCESS_TOKEN_RECEIVED: {
        AuthService.onAccessTokenReceived(listener);
        break;
      }
    }
  }

  public static removeEventListener(type: EventTypes, listener: () => void) {
    AuthService.subscribers[type] = AuthService.subscribers[type].filter(
      (activeListener) => activeListener !== listener
    );
  }
}
